import { Lightbulb, Moon, Sun } from 'lucide-react'
import { useTheme } from '../../lib/theme'
import { SwitchTrack } from '../ui'

// The reader's own controls, Reading light and light/dark, in one floating
// pill at the bottom right where a reader's hand already is, instead of small
// grey icons in the header. Where it sits around the Taken surfaces:
//
//   desktop (≥1024px)  Taken is a small "Taken · N" pill, bottom right (a
//                      340px drawer when open). This pill stacks directly
//                      above it, and slides left of the drawer while that's
//                      open (the Taken pill is under the drawer then).
//   phones             Taken is the floating "Taken · N" button, bottom right,
//                      over the Join bar when signed out. The pill stacks
//                      directly above whichever of those is showing.
//
// The caller knows which are showing, so it passes the clearances (px).

/** How far the pill sits from the right edge and the bottom, before the safe area. */
export interface DockClearance {
  right: number
  bottom: number
}

/** Height of the phones' floating Taken button, plus the gap above it. */
const FAB_STACK = 56

/** Height of the desktop Taken pill (40px), plus the gap above it. */
const PILL_STACK = 50

/** Gap from the screen edges. */
const EDGE = 16

/**
 * Clearances for the pill. Desktop: above the Taken pill while it shows, or
 * past the open drawer (340px). Phones: above the Join bar and the Taken button.
 */
export function dockClearance({
  desk,
  takenPill = false,
  drawerOpen = false,
  joinBar = 0,
  takenFab = false,
}: {
  desk: boolean
  takenPill?: boolean
  drawerOpen?: boolean
  joinBar?: number
  takenFab?: boolean
}): DockClearance {
  if (desk) return drawerOpen ? { right: EDGE + 340, bottom: 20 } : { right: EDGE, bottom: 20 + (takenPill ? PILL_STACK : 0) }
  return { right: EDGE, bottom: 20 + joinBar + (takenFab ? FAB_STACK : 0) }
}

export function ReadingControls({
  readingLight,
  clearance,
}: {
  /** Omit where there's no article to light (followers-only). */
  readingLight?: { on: boolean; onChange(on: boolean): void }
  clearance: DockClearance
}) {
  const { theme, toggleTheme } = useTheme()
  const dark = theme === 'dark'
  return (
    <div
      role="group"
      aria-label="Reading controls"
      data-reading-controls=""
      style={{ right: clearance.right, bottom: `calc(${clearance.bottom}px + env(safe-area-inset-bottom))` }}
      className="fixed z-30 flex items-center gap-0.5 rounded-full border border-border/60 bg-card/95 p-1 font-sans text-[13px] font-medium text-foreground shadow-[var(--shadow-chip)] backdrop-blur-[8px] transition-[right,bottom] duration-200"
    >
      {readingLight && (
        <>
          <button
            type="button"
            role="switch"
            aria-checked={readingLight.on}
            aria-label="Reading light"
            title="Reading light: dim everything but the paragraph you're on"
            onClick={() => readingLight.onChange(!readingLight.on)}
            className="flex items-center gap-2 rounded-full py-1.5 pl-2.5 pr-2 transition-colors hover:bg-muted/40"
          >
            <Lightbulb size={16} aria-hidden className={readingLight.on ? 'text-primary' : 'text-muted-foreground'} />
            <span aria-hidden className="hidden sm:inline">
              Reading light
            </span>
            <SwitchTrack on={readingLight.on} />
          </button>
          <span aria-hidden className="mx-0.5 h-5 w-px bg-border/70" />
        </>
      )}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={dark ? 'Light mode' : 'Dark mode'}
        title={dark ? 'Light mode' : 'Dark mode'}
        className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
      >
        {dark ? <Sun size={17} aria-hidden /> : <Moon size={17} aria-hidden />}
      </button>
    </div>
  )
}
